import { z } from "zod";
import { RequiredFirestoreTimestampSchema } from "./common.schema";

/**
 * Zod schema for Page documents in Firestore
 * Path: organizations/{orgId}/domains/{domainId}/pages/{pageId}
 */
const PageDocBaseSchema = z.object({
    url: z.string().default(""),
    source: z.enum(["manual", "wordpress", "observation"]).default("manual"),
    title: z.string().default(""),
    input_keyword: z.string().default(""),
    idea_id: z.string().nullable().optional(),
    live_version_id: z.string().nullable().default(null),
    draft_version_id: z.string().nullable().default(null),
    last_published_version_id: z.string().nullable().default(null),
    updated_at: RequiredFirestoreTimestampSchema.nullable().default(null),
    created_by: z.string().min(1, "Page author is required"),
    updated_by: z.string().nullable().optional(),
    custom_instructions: z.record(z.string(), z.string()).default({}),
    wordpress: z
      .object({
        wp_id: z.number().nullable().optional(),
        wp_type: z.enum(["page", "post"]).optional(),
        wp_status: z.string().default(""),
        wp_slug: z.string().default(""),
        wp_type_label: z.string().default(""),
        wp_modified: RequiredFirestoreTimestampSchema.nullable().default(null),
        wp_parent_id: z.number().nullable().default(null),
      })
      .nullable()
      .default(null),
    created_at: RequiredFirestoreTimestampSchema.nullable().default(null),
  });

export const PageSchema = PageDocBaseSchema.refine(
  (data) => data.source === "wordpress" || data.url.length > 0,
  {
    message: "Manual pages must have a URL",
    path: ["url"],
  },
);

export const PageEntitySchema = PageDocBaseSchema.extend({
  id: z.string().min(1, "Page ID is required"),
  domainId: z.string().min(1, "Domain ID is required"),
}).refine((data) => data.source === "wordpress" || data.url.length > 0, {
  message: "Manual pages must have a URL",
  path: ["url"],
});

/**
 * TypeScript type inferred from Zod schema
 * This replaces the old Page type from src/types/Page.ts
 */
export type PageDoc = z.infer<typeof PageSchema>;
export type Page = z.infer<typeof PageEntitySchema>;

/**
 * Parse function that validates and throws on error (strict mode)
 * Use this in store slices for data validation
 */
export const parsePage = (data: unknown): Page => {
  return PageEntitySchema.parse(data);
};

/**
 * Safe parse function that returns success/error object
 * Use this when you want to handle validation errors gracefully
 */
export const safePageParse = (data: unknown) => {
  return PageEntitySchema.safeParse(data);
};
